// ============================================================
// DashboardPanels.tsx
// "Your next deadlines" table + the admin-only "Across the firm" panel,
// ported from the approved mockup (casevo-dashboard-premium.html, opt. 2):
//   card     radius 16 · border · surface · soft shadow
//   header   pad 20/24 · label 11.5px/.14em/600 · "Open calendar →" 13px/600
//   th row   grid 110px | 1fr | 200px | 90px · pad 10/24 · input band ·
//            hairlines · 11px/.12em/600 uppercase soft
//   empty    pad 34/24 · 46px green-tint ring + calendar-check · 15px/600
//            title · 13px soft sub
//   firm     label + "Admin only" pill (10px/.12em, #2d6a4f) · rows
//            pad 12/0 hairline-separated · 34×34 r10 icon tile · 14/500
//            label · 12px caption · 22px/600 value
// Data is the dashboard's existing derived values — nothing new fetched.
// Across the firm tiles are buttons when given a handler (Open cases → Cases
// › Open; Due this week → this list in firm-week mode; Fee-earners → Admin ›
// Team): pointer, hover tint and a focus ring; Enter/Space via the button.
// Without a handler a tile is plain (no pointer, no hover).
// ============================================================
import React from 'react'

const CARD = 'rounded-[14px] border border-casevo-border bg-casevo-surface shadow-[0_1px_2px_rgba(16,40,28,0.05),0_2px_6px_rgba(16,40,28,0.04)] dark:shadow-[0_1px_2px_rgba(0,0,0,0.3)]'
export const DASH_LBL = 'text-[11.5px] tracking-[0.14em] uppercase font-semibold text-casevo-brand dark:text-[#74c69d]'
const HAIR = 'border-casevo-border-soft'

const Ic: React.FC<{ d: string; size?: number; className?: string }> = ({ d, size = 16, className }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"
    dangerouslySetInnerHTML={{ __html: d }} />
)
export const ICONS = {
  arrow: '<line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/>',
  calcheck: '<rect x="3" y="4" width="18" height="18" rx="2"/><path d="M16 2v4M8 2v4M3 10h18"/><polyline points="9 16 11 18 15 14"/>',
  brief: '<rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/>',
  users: '<path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>',
  alert: '<circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>',
}

export const LinkButton: React.FC<{ onClick: () => void; children: React.ReactNode }> = ({ onClick, children }) => (
  <button type="button" onClick={onClick}
    className="inline-flex items-center gap-[5px] text-[13px] font-semibold whitespace-nowrap text-casevo-brand dark:text-[#74c69d] hover:underline underline-offset-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-casevo-mid rounded-sm">
    {children}<Ic d={ICONS.arrow} size={14} />
  </button>
)

// ── YOUR NEXT DEADLINES ─────────────────────────────────────
export interface DeadlineRowData {
  key: string
  title: string
  typeLabel: string      // "Task" | "Deadline"
  caseLabel: string
  when: string           // "Today" / "Tomorrow" / "in 3d"
  dateLabel: string      // "Mon 28 Sep"
  urgency: 'overdue' | 'soon' | 'later'
  onClick?: () => void
}
interface NextDeadlinesCardProps {
  rows: DeadlineRowData[]
  totalCount: number
  isLoading: boolean
  error: boolean
  onOpenCalendar: () => void
  /** Opened from the Due soon tile's "N deadlines": every case deadline in
   *  the 7-day window (rows already narrowed + uncapped by the caller). */
  deadlinesOnly?: boolean
  onShowAll?: () => void
  /** Opened from Across the firm › Due this week: every firm deadline + task
   *  in the same window as the tile (rows narrowed + uncapped by the caller). */
  firmWeek?: boolean
  onLeaveFirmWeek?: () => void
  sectionRef?: React.Ref<HTMLElement>
}

const STATUS: Record<DeadlineRowData['urgency'], { label: string; cls: string }> = {
  overdue: { label: 'Overdue', cls: 'bg-[#fbeae7] text-[#c23b2c] dark:bg-[rgba(243,160,148,0.12)] dark:text-[#f3a094]' },
  soon: { label: 'Soon', cls: 'bg-[#fbf1dd] text-[#b0720f] dark:bg-[rgba(241,192,106,0.12)] dark:text-[#f1c06a]' },
  later: { label: 'Upcoming', cls: 'bg-[#e3f2ea] text-[#2d6a4f] dark:bg-[rgba(127,209,168,0.12)] dark:text-[#7fd1a8]' },
}
const GRID = 'grid grid-cols-[1fr_auto] sm:grid-cols-[110px_1fr_200px_90px] items-center gap-x-4'

export const NextDeadlinesCard: React.FC<NextDeadlinesCardProps> = ({ rows, totalCount, isLoading, error, onOpenCalendar, deadlinesOnly = false, onShowAll, firmWeek = false, onLeaveFirmWeek, sectionRef }) => (
  <section ref={sectionRef} tabIndex={-1} aria-label={firmWeek ? 'Firm deadlines and tasks in the next 7 days' : deadlinesOnly ? 'Case deadlines in the next 7 days' : 'Your next deadlines'}
    className={`${CARD} overflow-hidden min-w-0 focus:outline-none`}>
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 px-6 py-5">
      <span className={DASH_LBL}>{firmWeek ? `Across the firm · due this week (${totalCount})` : deadlinesOnly ? `Case deadlines · next 7 days (${totalCount})` : 'Your next deadlines'}</span>
      {firmWeek && onLeaveFirmWeek && (
        <button type="button" onClick={onLeaveFirmWeek}
          className="text-[12px] font-semibold text-casevo-text-soft hover:text-casevo-forest underline underline-offset-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-casevo-mid rounded-sm">
          Back to your deadlines
        </button>
      )}
      {deadlinesOnly && onShowAll && (
        <button type="button" onClick={onShowAll}
          className="text-[12px] font-semibold text-casevo-text-soft hover:text-casevo-forest underline underline-offset-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-casevo-mid rounded-sm">
          Show tasks too
        </button>
      )}
      <span className="flex-1" />
      <LinkButton onClick={onOpenCalendar}>Open calendar</LinkButton>
    </div>
    <div className={`${GRID} hidden sm:grid px-6 py-2.5 bg-[#f2f8f5] dark:bg-[#2d3e34] border-y ${HAIR}
                    text-[11px] tracking-[0.12em] uppercase text-casevo-text-soft font-semibold`}>
      <span>Due</span><span>Item</span><span>Case</span><span className="text-right">Status</span>
    </div>

    {isLoading ? (
      // Four rows the height of real (two-line) rows — the list shows up to 4.
      <div aria-busy="true">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className={`px-6 py-3.5 border-t ${HAIR} first:border-t-0`}>
            <div className="h-[37px] flex flex-col justify-center gap-2">
              <div className="h-3.5 rounded bg-[#e8f1ec] dark:bg-[#31443a] animate-pulse motion-reduce:animate-none" style={{ width: `${80 - i * 10}%` }} />
              <div className="h-2.5 w-24 rounded bg-[#e8f1ec] dark:bg-[#31443a] animate-pulse motion-reduce:animate-none" />
            </div>
          </div>
        ))}
      </div>
    ) : error ? (
      <div className="px-6 py-[34px] flex flex-col items-center text-center gap-2.5">
        <span className="w-[46px] h-[46px] rounded-full bg-[#fbeae7] text-[#c23b2c] dark:bg-[rgba(243,160,148,0.12)] dark:text-[#f3a094] flex items-center justify-center">
          <Ic d={ICONS.alert} size={20} />
        </span>
        <div className="text-[15px] font-semibold text-casevo-forest">Couldn&apos;t load deadlines</div>
        <div className="text-[13px] text-casevo-text-soft">Try refreshing the page.</div>
      </div>
    ) : rows.length === 0 ? (
      <div className="px-6 py-[34px] flex flex-col items-center text-center gap-2.5">
        <span className="w-[46px] h-[46px] rounded-full bg-[#e3f2ea] text-[#2d6a4f] dark:bg-[rgba(127,209,168,0.12)] dark:text-[#7fd1a8] flex items-center justify-center">
          <Ic d={ICONS.calcheck} size={20} />
        </span>
        <div className="text-[15px] font-semibold text-casevo-forest">{firmWeek ? 'Nothing due across the firm this week' : deadlinesOnly ? 'No case deadlines in the next 7 days' : 'Nothing due this week'}</div>
        <div className="text-[13px] text-casevo-text-soft">{firmWeek ? 'Firm deadlines and tasks due in the next 7 days will list here.' : deadlinesOnly ? 'Open case deadlines due in the next 7 days will list here.' : <>You&apos;re clear — deadlines and tasks for the next 7 days will list here.</>}</div>
      </div>
    ) : (
      <div>
        {rows.map((r) => {
          const Row: React.ElementType = r.onClick ? 'button' : 'div'
          return (
            <Row key={r.key} {...(r.onClick ? { type: 'button', onClick: r.onClick } : {})}
              className={`${GRID} w-full text-left px-6 py-3.5 border-t ${HAIR} first:border-t-0 sm:first:border-t-0
                          ${r.onClick ? 'hover:bg-[#f2f8f5] dark:hover:bg-[#2d3e34] transition-colors focus:outline-none focus-visible:bg-[#f2f8f5] dark:focus-visible:bg-[#2d3e34]' : ''}`}>
              <span className="hidden sm:block min-w-0">
                <span className="block text-[13px] font-semibold text-casevo-forest">{r.when}</span>
                <span className="block text-[11.5px] text-casevo-text-soft">{r.dateLabel}</span>
              </span>
              <span className="min-w-0">
                <span className="block text-[14px] text-casevo-forest truncate">{r.title}</span>
                <span className="block text-[11.5px] text-casevo-text-soft truncate">
                  <span className="sm:hidden">{r.when} · </span>{r.typeLabel}<span className="sm:hidden">{r.caseLabel ? ` · ${r.caseLabel}` : ''}</span>
                </span>
              </span>
              <span className="hidden sm:block text-[13px] text-casevo-text-soft truncate">{r.caseLabel || '—'}</span>
              <span className="text-right">
                <span className={`inline-block text-[11px] font-semibold px-2 py-0.5 rounded-full ${STATUS[r.urgency].cls}`}>{STATUS[r.urgency].label}</span>
              </span>
            </Row>
          )
        })}
        {totalCount > rows.length && !firmWeek && (
          <div className={`px-6 py-3 border-t ${HAIR} text-[12.5px] text-casevo-text-soft`}>
            +{totalCount - rows.length} more in the next 7 days
          </div>
        )}
      </div>
    )}
  </section>
)

// ── ACROSS THE FIRM (admin only) ────────────────────────────
interface FirmPanelProps {
  openCases: number | null
  dueThisWeek: number | null
  feeEarners: number | null
  errors: { firmCases: boolean; calendar: boolean; firmUsers: boolean }
  onOpenCases?: () => void
  onDueThisWeek?: () => void
  // Admins only (the caller passes it only for an admin): without it the tile
  // isn't clickable at all.
  onFeeEarners?: () => void
}
// Clickable tile: the "Due soon" tile's behaviour on a light card — pointer,
// a hover tint, a visible focus ring, Enter/Space (a real button).
const TILE_BTN = 'w-full text-left rounded-[10px] -mx-2 px-2 cursor-pointer transition-colors hover:bg-casevo-input focus:outline-none focus-visible:ring-2 focus-visible:ring-casevo-mid'
export const FirmPanel: React.FC<FirmPanelProps> = ({ openCases, dueThisWeek, feeEarners, errors, onOpenCases, onDueThisWeek, onFeeEarners }) => {
  const rows: [string, number | null, string, string, boolean, (() => void) | undefined, string][] = [
    ['Open cases', openCases, ICONS.brief, 'Across all fee-earners', errors.firmCases, onOpenCases, 'open the case list filtered to open cases'],
    ['Due this week', dueThisWeek, ICONS.calcheck, 'Deadlines + tasks, next 7 days', errors.calendar, onDueThisWeek, 'show them'],
    ['Fee-earners', feeEarners, ICONS.users, 'Active users', errors.firmUsers, onFeeEarners, 'open Admin › Team'],
  ]
  return (
    <section className={`${CARD} px-[22px] py-[22px] min-w-0`}>
      <div className="flex items-center gap-2.5">
        <span className={DASH_LBL}>Across the firm</span>
        <span className="text-[10px] tracking-[0.12em] uppercase px-2 py-[3px] rounded-[5px] bg-[#2d6a4f] text-white font-semibold">Admin only</span>
      </div>
      <div className="mt-1.5">
        {rows.map(([label, value, icon, caption, err, onClick, does], i) => {
          const Tile: React.ElementType = onClick ? 'button' : 'div'
          return (
          <div key={label} className={i ? `border-t ${HAIR}` : ''}>
          <Tile {...(onClick ? { type: 'button', onClick, 'aria-label': `${label}: ${value ?? 'unknown'} — ${does}` } : {})}
            className={`flex items-center gap-3 py-3 ${onClick ? TILE_BTN : ''}`}>
            <span className="w-[34px] h-[34px] rounded-[10px] bg-[#f2f8f5] dark:bg-[#2d3e34] text-casevo-mid flex items-center justify-center shrink-0">
              <Ic d={icon} />
            </span>
            <div className="flex-1 min-w-0">
              <div className="text-[14px] font-medium text-casevo-forest">{label}</div>
              <div className="text-[12px] text-casevo-text-soft">{err ? "Couldn't load" : caption}</div>
            </div>
            <div className={`text-[22px] font-semibold text-casevo-forest tabular-nums ${value === null ? 'opacity-40' : ''}`}>
              {value === null ? '—' : value}
            </div>
          </Tile>
          </div>
          )
        })}
      </div>
    </section>
  )
}
